import React from 'react';
import '../../css/from.css';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import { Form, Input, Button, Typography, Row, Col } from 'antd';
import { MinusCircleOutlined, PlusOutlined, GlobalOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { createGymkhanaContent, updateGymkhanaContent, selectGymkhanaSubmitStatus, resetSubmitStatus } from '../../redux/gymkhanaSlice';

const { TextArea } = Input;

const WhatIsGymkhanaForm = ({ initialValues, onSuccess }) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const isEditing = Boolean(initialValues?.id);
  const submitStatus = useSelector(selectGymkhanaSubmitStatus);

  React.useEffect(() => {
    form.resetFields();
    if (initialValues) {
      const formattedValues = {
        ...initialValues,
        competitionFormats: initialValues.competitionFormats ? (typeof initialValues.competitionFormats === 'string' ? JSON.parse(initialValues.competitionFormats) : initialValues.competitionFormats) : [],
        technicalElements: initialValues.technicalElements ? (typeof initialValues.technicalElements === 'string' ? JSON.parse(initialValues.technicalElements) : initialValues.technicalElements) : [],
        scoringElements: initialValues.scoringElements ? (typeof initialValues.scoringElements === 'string' ? JSON.parse(initialValues.scoringElements) : initialValues.scoringElements) : []
      };
      form.setFieldsValue(formattedValues);
    }
  }, [initialValues, form]);

  const onFinish = async (values) => {
    try {
      const dataToSubmit = {
        ...values,
        competitionFormats: JSON.stringify(values.competitionFormats),
        technicalElements: JSON.stringify(values.technicalElements),
        scoringElements: JSON.stringify(values.scoringElements)
      };

      if (isEditing) {
        await dispatch(updateGymkhanaContent({ id: initialValues.id, data: dataToSubmit })).unwrap();
        toast.success('Content updated successfully');
      } else {
        await dispatch(createGymkhanaContent(dataToSubmit)).unwrap();
        toast.success('Content created successfully');
      }
      onSuccess();
      dispatch(resetSubmitStatus());
    } catch (error) {
      console.error('Error:', error);
      toast.error(`Failed to ${isEditing ? 'update' : 'create'} content: ${error.message || error}`);
    }
  };

  return (
    <div className="about-form-wrapper">
      <div className="form-container">
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          requiredMark={false}
          initialValues={{
            competitionFormats: [],
            technicalElements: [],
            scoringElements: []
          }}
        >
          <Row gutter={[15, 15]}>
            <Col xs={24}>
              <div className="premium-card card-indigo" style={{ marginBottom: '0px' }}>
                <div className="card-header-tag">General</div>
                <Typography.Text className="section-label">General information</Typography.Text>
                <Typography.Text className="section-description">Manage the core definition and overview of Gymkhana.</Typography.Text>

                <Form.Item
                  name="title"
                  label="Title"
                  rules={[{ required: true, message: 'Please enter title' }]}
                >
                  <Input prefix={<GlobalOutlined />} placeholder="e.g. What is Gymkhana Motorsport?" />
                </Form.Item>

                <Form.Item
                  name="subtitle"
                  label="Introductory Subtitle"
                  rules={[{ required: true, message: 'Required' }]}
                >
                  <TextArea rows={4} placeholder="Brief summary of Gymkhana..." />
                </Form.Item>

                <Form.Item
                  name="description"
                  label="Detailed Description"
                  rules={[{ required: true, message: 'Required' }]}
                  style={{ marginBottom: 0 }}
                >
                  <TextArea rows={6} placeholder="In-depth explanation..." />
                </Form.Item>
              </div>
            </Col>

            <Col xs={24}>
              <Row gutter={[15, 0]} className="ant-row-stretch">
                <Col xs={24} lg={12}>
                  <div className="premium-card card-orange">
                    <div className="card-header-tag">Experience</div>
                    <Typography.Text className="section-label">Competition Formats</Typography.Text>
                    <Typography.Text className="section-description">Define the various ways Gymkhana events are structured.</Typography.Text>

                    <Form.List name="competitionFormats">
                      {(fields, { add, remove }) => (
                        <>
                          {fields.map(({ key, name, ...restField }) => (
                            <div key={key} className="characteristic-row">
                              <Form.Item
                                {...restField}
                                name={[name]}
                                rules={[{ required: true, message: 'Required' }]}
                                style={{ margin: 0, flex: 1 }}
                              >
                                <Input prefix={<CheckCircleOutlined />} placeholder="e.g. Solo Format" />
                              </Form.Item>
                              {fields.length > 1 && (
                                <Button
                                  type="text"
                                  danger
                                  icon={<MinusCircleOutlined />}
                                  onClick={() => remove(name)}
                                  style={{ display: 'flex', alignItems: 'center' }}
                                />
                              )}
                            </div>
                          ))}
                          <Button
                            type="dashed"
                            onClick={() => add()}
                            block
                            icon={<PlusOutlined />}
                            style={{ marginTop: 12, height: '40px', borderRadius: '8px' }}
                          >
                            Add competition format
                          </Button>
                        </>
                      )}
                    </Form.List>
                  </div>
                </Col>

                <Col xs={24} lg={12}>
                  <div className="premium-card card-green">
                    <div className="card-header-tag">Technical</div>
                    <Typography.Text className="section-label">Technical Elements</Typography.Text>
                    <Typography.Text className="section-description">Key skills and elements required for participation.</Typography.Text>

                    <Form.List name="technicalElements">
                      {(fields, { add, remove }) => (
                        <>
                          {fields.map(({ key, name, ...restField }) => (
                            <div key={key} className="characteristic-row">
                              <Form.Item
                                {...restField}
                                name={[name]}
                                rules={[{ required: true, message: 'Required' }]}
                                style={{ margin: 0, flex: 1 }}
                              >
                                <Input prefix={<CheckCircleOutlined />} placeholder="e.g. Handbrake Turn" />
                              </Form.Item>
                              {fields.length > 1 && (
                                <Button
                                  type="text"
                                  danger
                                  icon={<MinusCircleOutlined />}
                                  onClick={() => remove(name)}
                                  style={{ display: 'flex', alignItems: 'center' }}
                                />
                              )}
                            </div>
                          ))}
                          <Button
                            type="dashed"
                            onClick={() => add()}
                            block
                            icon={<PlusOutlined />}
                            style={{ marginTop: 12, height: '40px', borderRadius: '8px' }}
                          >
                            Add technical element
                          </Button>
                        </>
                      )}
                    </Form.List>
                  </div>
                </Col>
              </Row>
            </Col>

            <Col xs={24}>
              <div className="premium-card card-blue">
                <div className="card-header-tag">Criteria</div>
                <Typography.Text className="section-label">Scoring Elements</Typography.Text>
                <Typography.Text className="section-description">How performance is measured and judged in various formats.</Typography.Text>

                <Form.List name="scoringElements">
                  {(fields, { add, remove }) => (
                    <>
                      <Row gutter={[15, 15]}>
                        {fields.map(({ key, name, ...restField }) => (
                          <Col xs={24} sm={12} key={key}>
                            <div className="characteristic-row">
                              <Form.Item
                                {...restField}
                                name={[name]}
                                rules={[{ required: true, message: 'Required' }]}
                                style={{ margin: 0, flex: 1 }}
                              >
                                <Input prefix={<CheckCircleOutlined />} placeholder="e.g. Time Penalty" />
                              </Form.Item>
                              <Button
                                type="text"
                                danger
                                icon={<MinusCircleOutlined />}
                                onClick={() => remove(name)}
                                style={{ display: 'flex', alignItems: 'center' }}
                              />
                            </div>
                          </Col>
                        ))}
                      </Row>
                      <Button
                        type="dashed"
                        onClick={() => add()}
                        block
                        icon={<PlusOutlined />}
                        style={{ marginTop: 15, height: '40px', borderRadius: '8px' }}
                      >
                        Add scoring element
                      </Button>
                    </>
                  )}
                </Form.List>
              </div>
            </Col>
          </Row>

          {/* Action Area */}
          <div className="sticky-action-footer">
            <Button type="default" size="large" onClick={() => onSuccess()} style={{ borderRadius: '8px', height: '44px' }}>
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              loading={submitStatus === 'loading'}
              icon={isEditing ? <CheckCircleOutlined /> : <PlusOutlined />}
            >
              {isEditing ? 'Save changes' : 'Create profile'}
            </Button>
          </div>
          
          <div className="form-footer-spacer" />
        </Form>
      </div>
    </div>
  );
};

export default WhatIsGymkhanaForm;
